import { useEffect, useRef, useState } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

let scriptPromise = null;

const loadRecaptchaScript = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-kuthe-recaptcha]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.grecaptcha));
      existing.addEventListener("error", () => reject(new Error("reCAPTCHA script failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.kutheRecaptcha = "true";
    script.onload = () => {
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    };
    script.onerror = () => reject(new Error("reCAPTCHA script failed"));
    document.body.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Optional Google reCAPTCHA v2 for Netlify Forms.
 * Renders only when REACT_APP_RECAPTCHA_SITE_KEY is set. Pair with
 * data-netlify-recaptcha="true" on the matching public/index.html stub
 * once the key is live in Netlify.
 */
const NetlifyRecaptcha = ({ onChange }) => {
  const hostRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [failed, setFailed] = useState(false);
  const dim = useColorModeValue("#6a4020", "#a89880");

  onChangeRef.current = onChange;

  useEffect(() => {
    if (!SITE_KEY || !hostRef.current) return undefined;
    let cancelled = false;

    loadRecaptchaScript()
      .then((grecaptcha) => {
        if (cancelled || !hostRef.current || widgetIdRef.current !== null) return;
        widgetIdRef.current = grecaptcha.render(hostRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onChangeRef.current?.(token),
          "expired-callback": () => onChangeRef.current?.(""),
          "error-callback": () => {
            onChangeRef.current?.("");
            setFailed(true);
          },
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!SITE_KEY) return null;

  return (
    <Box w="full">
      <Box ref={hostRef} />
      {failed && (
        <Text fontSize="12px" color={dim} mt={1}>
          Captcha failed to load - refresh and try again.
        </Text>
      )}
    </Box>
  );
};

export const isRecaptchaEnabled = Boolean(SITE_KEY);

export default NetlifyRecaptcha;
